import { cn } from "@/lib/utils";

export interface IconButtonProps {
  /** Required — this is the accessible name AND the hover tooltip. */
  title: string;
  /** True when the action it represents is the current state (approved, flagged). */
  active?: boolean;
  /** Classes applied while `active` — usually a tone colour. */
  activeClassName?: string;
  /** Classes applied on hover — usually a tinted background + tone colour. */
  hoverClassName?: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * A bare icon-only action, sized to sit inline inside a dense table row.
 *
 * Deliberately not a `<Button variant="ghost" size="icon">`: that carries
 * button padding and a focus ring sized for standalone controls, which is too
 * heavy when three of them share a table cell. This is the row-action idiom
 * used by the reconciliation worklists (approve / flag / settle).
 *
 * It stops click propagation, so it's safe inside a clickable row or day cell.
 */
export function IconButton({
  title,
  active = false,
  activeClassName = "text-foreground",
  hoverClassName = "hover:bg-muted",
  onClick,
  disabled,
  className,
  children,
}: IconButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded disabled:opacity-40",
        !disabled && hoverClassName,
        active ? activeClassName : "text-muted-foreground",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}
